import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Send, Trash2, Save, Clock } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import api, { formatApiErrorDetail } from "../../lib/api";
import { RichToolbar } from "./RichToolbar";
import { ImageSlots } from "./ImageSlots";
import { uploadImage, naturalSize, pickFile } from "./editorUtils";

export const fmtDay = (iso) => { const [y, m, dd] = iso.split("-").map(Number); return new Date(y, m - 1, dd).toLocaleDateString("en-US", { month: "short", day: "numeric" }); };
const pct = (n, d) => (d ? `${((n / d) * 100).toFixed(1)}%` : "—");
const field = "w-full bg-brand-surface border border-brand-border rounded-md px-3 py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary";

function Stats({ s }) {
  if (!s || !s.sent) return null;
  const items = [["Sent", s.sent], ["Delivered", s.delivered], ["Opened", `${s.open} · ${pct(s.open, s.delivered)}`], ["Clicked", `${s.click} · ${pct(s.click, s.delivered)}`], ["Unsubscribed", s.unsubscribed || 0], ["Bounced", s.bounce || 0]];
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="nl-detail-stats">
      {items.map(([l, v]) => <div key={l} className="border border-brand-border bg-brand-surface rounded-md px-3 py-2"><p className="text-[10px] uppercase tracking-[0.12em] text-brand-tertiary">{l}</p><p className="text-sm text-brand-ink font-mono mt-0.5">{typeof v === "number" ? v.toLocaleString() : v}</p></div>)}
    </div>
  );
}

function Block({ b, html, ver, onChange, onImageClick }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && ref.current.innerHTML !== html) ref.current.innerHTML = html; }, [b.key, ver]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div ref={ref} contentEditable suppressContentEditableWarning data-testid={`nl-block-${b.key}`} data-block-key={b.key}
      onInput={(e) => onChange(b.key, e.currentTarget.innerHTML.replace(/ class="nl-img-selected"/g, ""))}
      onClick={(e) => onImageClick(e.target.tagName === "IMG" ? e.target : null)}
      className="prose prose-sm max-w-none bg-white border border-brand-border rounded-md px-4 py-3 focus:outline-none focus:border-brand-primary [&_h1]:text-lg [&_h1]:font-semibold [&_a]:text-brand-primary [&_a]:underline [&_img]:max-w-full [&_img]:cursor-pointer [&_img.nl-img-selected]:outline [&_img.nl-img-selected]:outline-2 [&_img.nl-img-selected]:outline-brand-primary" />
  );
}

function useRichEditing(setBlocks) {
  const range = useRef(null);
  const [img, setImg] = useState(null);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    const save = () => {
      const s = window.getSelection();
      if (s?.rangeCount && s.anchorNode?.parentElement?.closest("[data-block-key]")) range.current = s.getRangeAt(0).cloneRange();
    };
    document.addEventListener("selectionchange", save);
    return () => document.removeEventListener("selectionchange", save);
  }, []);
  const blockOf = (node) => (node?.nodeType === 1 ? node : node?.parentElement)?.closest("[data-block-key]");
  const emit = (el) => el && setBlocks((p) => ({ ...p, [el.dataset.blockKey]: el.innerHTML.replace(/ class="nl-img-selected"/g, "") }));
  const restore = () => {
    if (!range.current) { toast.message("Click into a text block first"); return null; }
    const el = blockOf(range.current.startContainer);
    el?.focus();
    const s = window.getSelection(); s.removeAllRanges(); s.addRange(range.current);
    return el;
  };
  const exec = (cmd, val) => {
    const el = restore(); if (!el) return;
    document.execCommand("styleWithCSS", false, cmd === "foreColor");
    document.execCommand(cmd, false, val);
    emit(el);
  };
  const selectImage = (node) => {
    document.querySelectorAll("img.nl-img-selected").forEach((n) => n.classList.remove("nl-img-selected"));
    if (node) node.classList.add("nl-img-selected");
    setImg(node);
  };
  const upload = async () => {
    const file = await pickFile(); if (!file) return null;
    setUploading(true);
    try { return await uploadImage(file); }
    catch (e) { toast.error("Image upload failed", { description: formatApiErrorDetail(e.response?.data?.detail), duration: 10000 }); return null; }
    finally { setUploading(false); }
  };
  const insertImage = async () => {
    const saved = range.current; const up = await upload(); if (!up) return;
    range.current = saved;
    exec("insertHTML", `<img src="${up.url}" alt="" style="max-width:100%;height:auto;display:block;margin:8px auto;" />`);
  };
  const replaceImage = async () => {
    const target = img; const up = await upload(); if (!up || !target) return;
    target.src = up.url; target.removeAttribute("srcset");
    if (target.getAttribute("height")) target.removeAttribute("height");
    emit(blockOf(target)); selectImage(null);
  };
  return { exec, selectImage, insertImage, replaceImage, imageSelected: !!img, uploading, upload,
    emoji: (em) => exec("insertText", em) };
}

function DraftEditor({ d, onSaved, onDeleted }) {
  const [form, setForm] = useState({ name: d.name, subject: d.subject, from_name: d.from_name || "" });
  const [blocks, setBlocks] = useState({});
  const [imgEdits, setImgEdits] = useState({});
  const [slotBusy, setSlotBusy] = useState(null);
  const rich = useRichEditing(setBlocks);
  const replaceSlot = async (im) => {
    setSlotBusy(im.key);
    const up = await rich.upload();
    setSlotBusy(null);
    if (!up) return;
    const nat = (up.width && up.height) ? { w: up.width, h: up.height } : await naturalSize(up.url);
    const width = im.width || nat?.w;
    const height = nat && width ? Math.round((width * nat.h) / nat.w) : im.height;
    setImgEdits((p) => ({ ...p, [im.key]: { src: up.url, width, height } }));
  };
  const [when, setWhen] = useState("");
  const [ver, setVer] = useState(0);
  const [fillMonth, setFillMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const fill = () => run(async () => {
    const { data } = await api.post(`/newsletters/emails/${d.id}/fill`, { month: fillMonth });
    setForm((f) => ({ ...f, subject: data.subject, name: data.name }));
    setBlocks((p) => ({ ...p, ...data.blocks }));
    setVer((v) => v + 1);
    const note = data.event_slots > data.events_available ? ` Only ${data.events_available} one-time event(s) for ${d.location} this month; add more in Events & Programming.` : "";
    toast.success(`Filled ${data.events_used} event slot(s). Review, then Save.`, { description: note || undefined });
  });
  const [busy, setBusy] = useState(false);
  const save = async (quiet) => {
    await api.patch(`/newsletters/emails/${d.id}`, { ...form, blocks, images: imgEdits });
    setBlocks({}); setImgEdits({});
    if (!quiet) toast.success("Saved to HubSpot");
    onSaved();
  };
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error("Something went wrong", { description: formatApiErrorDetail(e.response?.data?.detail) }); } finally { setBusy(false); } };
  const send = () => run(async () => {
    await save(true);
    if (when) {
      const [date, time] = when.split("T");
      await api.post(`/newsletters/emails/${d.id}/ready`, { send_date: date, send_time: time || null });
      toast.success(`Ready · sends ${fmtDay(date)}`, { description: "We'll email you and the location's managers that morning with a HubSpot link." });
      onSaved();
      return;
    }
    const tab = window.open("about:blank", "_blank");
    const { data } = await api.post(`/newsletters/emails/${d.id}/send`, { schedule_at: null });
    if (data.sent) { tab?.close(); toast.success("Sent"); onSaved(); return; }
    if (tab) tab.location.href = data.hubspot_url; else window.open(data.hubspot_url, "_blank", "noopener");
    toast.message("Opened in HubSpot", { description: "Click Send there to finish. The recipient list is already set." });
  });
  const unready = () => run(async () => { await api.delete(`/newsletters/emails/${d.id}/ready`); toast.success("Removed from the send queue"); onSaved(); });
  const del = () => run(async () => {
    if (!window.confirm("Delete this draft in HubSpot?")) return;
    await api.delete(`/newsletters/emails/${d.id}`); toast.success("Draft deleted"); onDeleted();
  });
  return (
    <div className="space-y-4" data-testid="nl-editor">
      {d.ready && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-brand-primary/30 bg-brand-primary/5 px-3 py-2.5" data-testid="nl-ready-banner">
          <p className="text-sm text-brand-ink"><span className="font-medium">Ready · sends {fmtDay(d.ready.send_date)}{d.ready.send_time ? ` at ${d.ready.send_time}` : ""}</span>
            <span className="text-brand-tertiary"> · reminder goes out that morning{d.ready.reminded_on ? ` (last sent ${fmtDay(d.ready.reminded_on)})` : ""}</span></p>
          <button onClick={unready} data-testid="nl-unready" className="text-xs text-brand-tertiary hover:text-brand-neg">Remove from queue</button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-brand-border px-3 py-2.5" data-testid="nl-fill">
        <span className="text-xs text-brand-muted">Fill LOCATION, MONTH, DATE and EVENT slots from</span>
        <input type="month" value={fillMonth} onChange={(e) => setFillMonth(e.target.value)} data-testid="nl-fill-month" className="border border-brand-border rounded-md px-2 py-1 text-sm bg-brand-surface" />
        <button onClick={fill} disabled={busy} data-testid="nl-fill-btn" className="rounded-md border border-brand-primary text-brand-primary px-3 py-1.5 text-xs hover:bg-brand-primary hover:text-brand-page">Fill from programming</button>
      </div>
      {[["name", "Internal name"], ["subject", "Subject line"], ["from_name", "From name"]].map(([k, l]) => (
        <label key={k} className="block"><span className="text-xs text-brand-muted">{l}</span>
          <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`nl-edit-${k}`} className={`${field} mt-1`} /></label>
      ))}
      <div className="space-y-2">
        <p className="text-xs text-brand-muted">Content blocks <span className="text-brand-tertiary">(click into text to edit; click an image inside text to replace it)</span></p>
        {d.blocks.length > 0 && <RichToolbar exec={rich.exec} onEmoji={rich.emoji} onInsertImage={rich.insertImage} onReplaceImage={rich.replaceImage} imageSelected={rich.imageSelected} busy={rich.uploading} />}
        {d.blocks.map((b) => <Block key={b.key} b={b} html={blocks[b.key] ?? b.html} ver={ver} onImageClick={rich.selectImage} onChange={(k, html) => setBlocks((p) => ({ ...p, [k]: html }))} />)}
        {!d.blocks.length && <p className="text-xs text-brand-tertiary">No editable text blocks. Edit the design in HubSpot.</p>}
      </div>
      <ImageSlots images={d.images} edits={imgEdits} onReplace={replaceSlot} busyKey={slotBusy} />
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-brand-border">
        <button onClick={() => run(() => save(false))} disabled={busy} data-testid="nl-save" className="inline-flex items-center gap-1.5 rounded-md border border-brand-border px-3 py-2 text-sm hover:border-brand-primary"><Save className="h-4 w-4" /> Save</button>
        <label className="inline-flex items-center gap-1.5 text-xs text-brand-muted"><Clock className="h-4 w-4" />
          <input type="datetime-local" value={when} min={new Date().toISOString().slice(0, 16)} onChange={(e) => setWhen(e.target.value)} data-testid="nl-schedule-at" className="border border-brand-border rounded-md px-2 py-1.5 text-sm bg-brand-surface" /></label>
        <button onClick={send} disabled={busy} data-testid="nl-send" className="inline-flex items-center gap-1.5 rounded-md bg-brand-ink text-brand-page px-4 py-2 text-sm hover:bg-brand-primary"><Send className="h-4 w-4" /> {when ? "Mark ready for this date" : "Send now in HubSpot"}</button>
        <button onClick={del} disabled={busy} data-testid="nl-delete" className="ml-auto inline-flex items-center gap-1 text-sm text-brand-tertiary hover:text-brand-neg"><Trash2 className="h-4 w-4" /> Delete</button>
      </div>
    </div>
  );
}

function Preview({ id, nonce }) {
  const [html, setHtml] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setHtml(null); setErr(null);
    api.get(`/newsletters/emails/${id}/preview-html`, { responseType: "text" })
      .then(({ data }) => setHtml(data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Preview unavailable"));
  }, [id, nonce]);
  if (err) return <p className="text-xs text-brand-tertiary" data-testid="nl-preview-error">{err}. Use Open in HubSpot to view it.</p>;
  if (!html) return <div className="w-full h-[70vh] bg-brand-hover animate-pulse rounded-md" />;
  return <iframe srcDoc={html} sandbox="allow-popups allow-popups-to-escape-sandbox" title="Email preview" data-testid="nl-preview" className="w-full h-[70vh] bg-white border border-brand-border rounded-md" />;
}

export function EmailDrawer({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!id) { setD(null); return; }
    api.get(`/newsletters/emails/${id}`).then(({ data }) => setD(data))
      .catch((e) => { toast.error("Couldn't open email", { description: formatApiErrorDetail(e.response?.data?.detail) }); onClose(); });
  }, [id, nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = () => { setNonce((n) => n + 1); onChanged(); };
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-brand-page w-full sm:max-w-3xl overflow-y-auto" data-testid="nl-drawer">
        {!d ? <p className="text-sm text-brand-tertiary mt-10">Loading…</p> : (
          <>
            <SheetHeader>
              <SheetTitle className="font-serif text-2xl font-normal text-brand-ink pr-6" data-testid="nl-detail-name">{d.name}</SheetTitle>
              <SheetDescription className="text-brand-muted">{d.subject} · <span className="capitalize">{d.state}</span>{d.from_name ? ` · from ${d.from_name}` : ""}</SheetDescription>
            </SheetHeader>
            <div className="mt-5 space-y-6">
              <Stats s={d.stats_full} />
              {d.state === "draft" && d.location && <DraftEditor d={d} onSaved={reload} onDeleted={() => { onChanged(); onClose(); }} />}
              {d.state === "draft" && !d.location && <p className="text-xs text-brand-gold">This draft was made in HubSpot and isn't tied to a location. Edit it there, or create a new one from the hub.</p>}
              <a href={d.hubspot_url} target="_blank" rel="noreferrer" data-testid="nl-open-hubspot" className="inline-flex items-center gap-1.5 text-sm text-brand-primary">Open in HubSpot <ExternalLink className="h-3.5 w-3.5" /></a>
              {d.preview_url && (
                <div>
                  <p className="text-xs text-brand-muted mb-2">Preview</p>
                  <Preview id={d.id} nonce={nonce} />
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
